import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Calendar, Clock, MapPin, Ticket, Share2, 
  Facebook, Instagram, MessageCircle, X 
} from "lucide-react";
import { API } from "@/config/api";

const EventCard = ({ event, onSelect }) => {
  const [tramosInfo, setTramosInfo] = useState({ enabled: false, tramo_actual: null, tramos: [] });
  const [showImageModal, setShowImageModal] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchTramos = async () => {
      try {
        const response = await axios.get(`${API}/events/${event.id}/tramos`);
        setTramosInfo(response.data);
      } catch (e) {
        // Silently fail
      }
    };
    if (event.tramos_enabled) {
      fetchTramos();
    }
  }, [event.id, event.tramos_enabled]);
  
  const handleShare = (e) => {
    e.stopPropagation();
    navigate(`/events/${event.id}`);
  };
  
  // Determine what to show for availability
  const getAvailabilityInfo = () => {
    if (tramosInfo.enabled && tramosInfo.tramo_actual) {
      const tramoActual = tramosInfo.tramos.find(t => t.nombre === tramosInfo.tramo_actual);
      if (tramoActual) {
        return {
          count: tramoActual.disponibles,
          label: tramosInfo.tramo_actual
        };
      }
    }
    return {
      count: event.remaining_tickets,
      label: 'disponibles'
    };
  };
  
  const availability = getAvailabilityInfo();
  const isSoldOut = availability.count <= 0;
  
  return (
    <div 
      className="event-card fade-in-up"
      data-testid={`event-card-${event.id}`}
    >
      {/* Image Modal */}
      {showImageModal && event.flyer_url && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setShowImageModal(false)}
        >
          <button 
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            onClick={() => setShowImageModal(false)}
          >
            <X size={24} className="text-white" />
          </button>
          <img 
            src={event.flyer_url} 
            alt={event.nombre}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {event.flyer_url && (
        <div 
          className="event-flyer relative cursor-pointer"
          onClick={() => setShowImageModal(true)}
        >
          <img src={event.flyer_url} alt={event.nombre} />
          {isSoldOut && <div className="sold-out-overlay">AGOTADO</div>}
          {/* Share button overlay */}
          <button
            onClick={(e) => { e.stopPropagation(); handleShare(e); }}
            className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-[var(--primary)] text-white rounded-full transition-all"
            title="Compartir evento"
            data-testid={`share-btn-${event.id}`}
          >
            <Share2 size={16} />
          </button>
        </div>
      )}
      {!event.flyer_url && (
        <div className="event-flyer-placeholder relative">
          <Ticket size={48} className="text-[var(--primary)]" />
          {isSoldOut && <div className="sold-out-overlay">AGOTADO</div>}
          <button
            onClick={handleShare}
            className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-[var(--primary)] text-white rounded-full transition-all"
            title="Compartir evento"
            data-testid={`share-btn-${event.id}`}
          >
            <Share2 size={16} />
          </button>
        </div>
      )}
      
      {event.pago_habilitado && event.precio > 0 ? (
        <span className="free-badge-below" style={{ background: 'linear-gradient(90deg, var(--primary), var(--accent))' }}>
          {event.precio.toFixed(2)} € / entrada
        </span>
      ) : (
        <span className="free-badge-below">Entrada Gratuita</span>
      )}
      
      <div className="event-card-content">
        <h3 className="event-card-title">{event.nombre}</h3>
        
        <div className="event-card-info">
          <div className="event-info-row">
            <MapPin size={14} />
            <span>{event.sala}</span>
          </div>
          <a 
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.direccion)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="event-info-row event-address-link"
            onClick={(e) => e.stopPropagation()}
          >
            <MapPin size={14} className="opacity-0" />
            <span className="text-xs underline hover:text-[var(--primary)]">{event.direccion}</span>
          </a>
          <div className="event-info-row">
            <Calendar size={14} />
            <span>{event.fecha}</span>
          </div>
          <div className="event-info-row">
            <Clock size={14} />
            <span>{event.hora}</span>
          </div>
          <div className="event-info-row text-xs text-[var(--accent-cyan)]">
            <Ticket size={12} />
            <span>Entrada válida hasta: {
              event.fecha_hora_evento 
                ? new Date(new Date(event.fecha_hora_evento).getTime() - 60000)
                    .toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) + 'h o completar aforo'
                : event.hora 
                    ? (() => {
                        const match = event.hora.match(/(\d{1,2}):(\d{2})/);
                        if (match) {
                          let hours = parseInt(match[1]);
                          let minutes = parseInt(match[2]) - 1;
                          if (minutes < 0) {
                            minutes = 59;
                            hours = hours - 1;
                            if (hours < 0) hours = 23;
                          }
                          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}h o completar aforo`;
                        }
                        return event.hora + ' o completar aforo';
                      })()
                    : 'N/A'
            }</span>
          </div>
        </div>
        
        <div className="event-card-footer flex-col gap-3">
          {/* Info de tickets o tramos */}
          {!tramosInfo.enabled ? (
            <div className="event-tickets-info w-full flex justify-between items-center pb-3 border-b border-[var(--border-color)]">
              <span className="text-xs text-[var(--text-muted)] uppercase tracking-wider">Entradas disponibles</span>
              <span className="tickets-remaining">{availability.count}</span>
            </div>
          ) : (
            <div className="w-full flex flex-wrap justify-center gap-2 pb-3 border-b border-[var(--border-color)]">
              {tramosInfo.tramos.filter(t => t.activo).map((tramo, idx) => (
                <span 
                  key={idx} 
                  className={`text-xs px-2 py-1 rounded ${tramo.agotado ? 'bg-gray-800 text-gray-500 line-through' : 'bg-[var(--surface-highlight)] text-[var(--text-muted)]'}`}
                >
                  {tramo.nombre}{tramo.agotado ? ' (Agotado)' : ''}
                </span>
              ))}
            </div>
          )}
          
          {/* Stack de botones vertical */}
          <div className="flex flex-col gap-2 w-full">
            {/* Botón Reservar - Grande con efecto 3D */}
            <button 
              className={`group relative w-full py-4 text-base font-bold uppercase tracking-widest transition-all duration-300 overflow-hidden ${
                isSoldOut 
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-[var(--primary)] via-[#ff1493] to-[var(--primary)] bg-[length:200%_100%] text-white hover:bg-right hover:shadow-[0_8px_25px_rgba(236,0,232,0.5)] hover:-translate-y-1 active:translate-y-0'
              }`}
              onClick={() => !isSoldOut && onSelect(event)}
              disabled={isSoldOut}
              data-testid={`reserve-btn-${event.id}`}
              style={{ backgroundPosition: 'left center' }}
            >
              {/* Efecto brillo animado */}
              {!isSoldOut && (
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              )}
              <span className="relative flex items-center justify-center gap-3">
                <Ticket size={20} className={!isSoldOut ? 'group-hover:rotate-12 transition-transform' : ''} />
                {isSoldOut ? 'Agotado' : 'Reservar Entradas'}
              </span>
            </button>
            
            {/* Botón Compartir - Con iconos de redes */}
            <button 
              onClick={handleShare}
              className="group relative w-full py-3 bg-[var(--surface-highlight)] border border-[var(--accent-cyan)]/30 text-[var(--accent-cyan)] hover:bg-[var(--accent-cyan)] hover:text-black transition-all duration-300 overflow-hidden"
              title="Compartir en redes"
              data-testid={`share-link-${event.id}`}
            >
              <span className="relative flex items-center justify-center gap-4">
                <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider">
                  <Share2 size={16} className="group-hover:scale-110 transition-transform" />
                  Compartir
                </span>
                {/* Iconos de redes sociales */}
                <span className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                  <MessageCircle size={14} className="group-hover:scale-125 group-hover:-rotate-12 transition-all duration-300" />
                  <Instagram size={14} className="group-hover:scale-125 transition-all duration-300 delay-75" />
                  <Facebook size={14} className="group-hover:scale-125 group-hover:rotate-12 transition-all duration-300 delay-150" />
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
